<script>
import Layout from "../../layouts/main";
import PageHeader from "../../components/page-header";

export default {
    components: { Layout, PageHeader },
    data() {
        return {
            title: "Listado de Digitalizados",
            items: [
                { text: "Digitalización", href: "/web/digitalizar" },
                { text: "Listado de Digitalizados", active: true },
            ],
            usuarios: [],
            currentPage: 1,
            perPage: 25,
            pageOptions: [10, 25, 50, 100],
            filtroRut: "",
            loading: true,
            loadError: "",
            totalRegistros: 0,
            totalDisponibles: 0,
            imagenSeleccionada: null,
            loadSequence: 0,
        };
    },
    computed: {
        registros() {
            return this.usuarios
                .flatMap((item) => item.records || [])
                .sort((left, right) => Number(right.id) - Number(left.id));
        },
        registrosFiltrados() {
            const filtro = this.normalizarDocumento(this.filtroRut);
            if (!filtro) return this.registros;

            return this.registros.filter((record) =>
                this.normalizarDocumento(record.rut).includes(filtro)
            );
        },
        registrosPaginados() {
            const inicio = (this.currentPage - 1) * this.perPage;
            return this.registrosFiltrados.slice(inicio, inicio + this.perPage);
        },
    },
    watch: {
        filtroRut() {
            this.currentPage = 1;
        },
        perPage() {
            this.currentPage = 1;
        },
    },
    created() {
        if (!this.user || this.user.rol !== "Admin") {
            window.location.href = "/";
            return;
        }

        this.cargarRegistros();
    },
    methods: {
        normalizarDocumento(value) {
            return String(value || "").toUpperCase().replace(/[^0-9K]/g, "");
        },
        async cargarRegistros() {
            const requestId = ++this.loadSequence;
            this.loading = true;
            this.loadError = "";

            try {
                const response = await axios.get("/api/get_ruts_unicos", {
                    params: {
                        page: 1,
                        per_page: 5000,
                    },
                });
                if (requestId !== this.loadSequence) return;

                this.usuarios = Array.isArray(response.data?.usuarios) ? response.data.usuarios : [];
                this.totalRegistros = this.registros.length;
                this.totalDisponibles = Number(response.data?.total_registros || 0);
            } catch (error) {
                if (requestId !== this.loadSequence) return;

                this.usuarios = [];
                this.totalRegistros = 0;
                this.totalDisponibles = 0;
                this.loadError = error.response?.data?.message || error.message || "No se pudo cargar el listado.";
            } finally {
                if (requestId === this.loadSequence) {
                    this.loading = false;
                }
            }
        },
        urlImagen(record) {
            return record.url_publica || record.pdf_url || "";
        },
        esPdf(url) {
            return /\.pdf(?:[?#]|$)/i.test(String(url || ""));
        },
        versionTexto(record) {
            if (record.tipo_mime === "PlantillaAsistente") return "Plantilla";
            if (["AsistenteQR", "AsistenteQRDescartado"].includes(record.tipo_mime)) return "QR";
            return record.formulario_version ? `V${record.formulario_version}` : "N/A";
        },
        verImagen(record) {
            const url = this.urlImagen(record);
            if (!url) return;

            this.imagenSeleccionada = { ...record, url };
            this.$bvModal.show("modal-imagen-digitalizada");
        },
        abrirImagen() {
            if (this.imagenSeleccionada?.url) {
                window.open(this.imagenSeleccionada.url, "_blank", "noopener");
            }
        },
        formatFecha(fecha) {
            if (!fecha) return "N/A";

            return new Date(fecha.replace(" ", "T")).toLocaleString("es-CL", {
                dateStyle: "short",
                timeStyle: "short",
            });
        },
        crmEstadoTexto(estado) {
            const labels = {
                enviado_ok: "Exitoso",
                reenviado_admin_exitoso: "Reenviado exitoso",
                duplicado: "Duplicado",
                reenviado_admin_duplicado: "Duplicado",
                error_envio: "Con error",
                reenviado_admin_error: "Con error",
                pendiente_envio: "Pendiente",
                pendiente_envio_admin: "Pendiente",
                pendiente_reenvio: "Pendiente",
                reenvio_programado: "Pendiente",
                sin_preparar: "Sin preparar",
            };

            return labels[estado] || estado || "Sin estado";
        },
        crmEstadoVariant(estado) {
            if (["enviado_ok", "reenviado_admin_exitoso"].includes(estado)) return "success";
            if (["duplicado", "reenviado_admin_duplicado"].includes(estado)) return "warning";
            if (["error_envio", "reenviado_admin_error"].includes(estado)) return "danger";
            if (["pendiente_envio", "pendiente_envio_admin", "pendiente_reenvio", "reenvio_programado"].includes(estado)) return "info";
            return "secondary";
        },
    },
};
</script>

<template>
    <Layout>
        <PageHeader :title="title" :items="items" />

        <div class="card">
            <div class="card-body">
                <div class="d-flex flex-wrap align-items-end justify-content-between mb-3">
                    <div class="form-group mb-2 mr-3 rut-search">
                        <label for="filtro-rut" class="mb-1">Buscar por RUT</label>
                        <div class="input-group">
                            <div class="input-group-prepend">
                                <span class="input-group-text"><i class="bx bx-search"></i></span>
                            </div>
                            <input
                                id="filtro-rut"
                                v-model.trim="filtroRut"
                                type="search"
                                class="form-control"
                                placeholder="Ej. 22.710.378-4"
                                autocomplete="off"
                            />
                        </div>
                    </div>
                    <div class="d-flex align-items-center mb-2">
                        <label class="d-flex align-items-center mb-0 mr-3">
                            Mostrar&nbsp;
                            <b-form-select v-model="perPage" size="sm" :options="pageOptions"></b-form-select>
                        </label>
                        <button class="btn btn-outline-secondary btn-sm" type="button" @click="cargarRegistros">
                            <i class="bx bx-refresh mr-1"></i>Actualizar
                        </button>
                    </div>
                </div>

                <div v-if="loadError" class="alert alert-danger">{{ loadError }}</div>

                <div v-if="loading" class="text-center py-5">
                    <b-spinner variant="primary"></b-spinner>
                    <p class="text-muted mt-3 mb-0">Cargando registros digitalizados...</p>
                </div>

                <template v-else>
                    <p class="text-muted small mb-2">
                        Mostrando {{ registrosPaginados.length }} de {{ registrosFiltrados.length }} registros recientes
                        <span v-if="totalDisponibles > totalRegistros">({{ totalDisponibles }} del año)</span>
                    </p>

                    <div v-if="registrosPaginados.length" class="table-responsive">
                        <table class="table table-hover table-sm mb-0">
                            <thead class="thead-light">
                                <tr>
                                    <th>Imagen</th>
                                    <th>RUT / Documento</th>
                                    <th>Pasaporte</th>
                                    <th>Nombre</th>
                                    <th>Versión</th>
                                    <th>Laborante</th>
                                    <th>Fecha revisión</th>
                                    <th>Evento</th>
                                    <th>CRM</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="record in registrosPaginados" :key="record.id">
                                    <td>
                                        <button
                                            v-if="urlImagen(record)"
                                            type="button"
                                            class="btn btn-outline-primary btn-sm"
                                            title="Ver imagen asociada"
                                            @click="verImagen(record)"
                                        >
                                            <i class="bx bx-image"></i>
                                        </button>
                                        <span v-else class="text-muted">N/A</span>
                                    </td>
                                    <td class="font-weight-semibold text-nowrap">{{ record.rut }}</td>
                                    <td>
                                        <span :class="record.es_pasaporte ? 'badge badge-warning' : 'badge badge-light'">
                                            {{ record.es_pasaporte ? 'Sí' : 'No' }}
                                        </span>
                                    </td>
                                    <td>{{ record.name }}</td>
                                    <td>{{ versionTexto(record) }}</td>
                                    <td>
                                        {{ record.laborante }}
                                        <small v-if="record.laborante_id" class="d-block text-muted">ID {{ record.laborante_id }}</small>
                                    </td>
                                    <td class="text-nowrap">{{ formatFecha(record.fecha_revision) }}</td>
                                    <td>{{ record.evento_codigo }}</td>
                                    <td>
                                        <span
                                            class="badge"
                                            :class="`badge-${crmEstadoVariant(record.estado_crm)}`"
                                            :title="record.crm_message || ''"
                                        >{{ crmEstadoTexto(record.estado_crm) }}</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <div v-else class="text-center text-muted py-5">
                        <i class="bx bx-search-alt display-4"></i>
                        <p class="mt-2 mb-0">No se encontraron registros para ese RUT.</p>
                    </div>

                    <div v-if="registrosFiltrados.length > perPage" class="d-flex justify-content-end mt-3">
                        <b-pagination
                            v-model="currentPage"
                            :total-rows="registrosFiltrados.length"
                            :per-page="perPage"
                            class="mb-0"
                        ></b-pagination>
                    </div>
                </template>
            </div>
        </div>

        <b-modal
            id="modal-imagen-digitalizada"
            size="xl"
            :title="imagenSeleccionada ? `Registro #${imagenSeleccionada.id} - ${imagenSeleccionada.rut}` : 'Imagen digitalizada'"
            hide-footer
        >
            <div v-if="imagenSeleccionada" class="digitalized-preview">
                <iframe
                    v-if="esPdf(imagenSeleccionada.url)"
                    :src="imagenSeleccionada.url"
                    title="Documento digitalizado"
                ></iframe>
                <img
                    v-else
                    :src="imagenSeleccionada.url"
                    :alt="`Imagen asociada al registro ${imagenSeleccionada.id}`"
                />
                <button type="button" class="btn btn-outline-primary btn-sm mt-3" @click="abrirImagen">
                    <i class="bx bx-link-external mr-1"></i>Abrir en otra pestaña
                </button>
            </div>
        </b-modal>
    </Layout>
</template>

<style scoped>
.rut-search {
    width: min(100%, 360px);
}

.digitalized-preview {
    text-align: center;
}

.digitalized-preview img,
.digitalized-preview iframe {
    width: 100%;
    height: min(70vh, 820px);
    border: 1px solid #d9dee7;
    object-fit: contain;
    background: #f8f9fa;
}
</style>
